import { FC } from 'react'
import { EChartOption } from 'echarts'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/lightbulb-line-24.svg'
import { EChart } from 'src/charts/EChart'
import styles from './sentimentSplit.module.scss'
import {
  SentimentPeriod,
  SentimentSplitData,
} from '../interfaces/comparison.interface'

interface SentimentSplitProps {
  data: SentimentSplitData
}

const COPY = {
  subtitle: 'Share of positive, neutral and negative mentions.',
  title: 'Sentiment Split',
}

// Figma's palette for the three bands. Positive is the app's purple; negative
// is a softer orange than the #FF6833 used for sentiment elsewhere, so it is
// declared here rather than pulled from the shared variables.
const SERIES = [
  { color: '#8080ff', key: 'positive', label: 'Positive' },
  { color: '#d9dde1', key: 'neutral', label: 'Neutral' },
  { color: '#f08159', key: 'negative', label: 'Negative' },
] as const

const BAR_WIDTH = 72
const CHART_HEIGHT = 304
// Only the top of the stack is rounded, so the three bands still read as one
// column. The band that carries it is the last series drawn.
const BAR_RADIUS: [number, number, number, number] = [12, 12, 0, 0]

// The band that ends a column, i.e. the last one carrying any mentions. It is
// the one that gets the rounded top, whatever the palette's order.
const topBandOf = (period: SentimentPeriod) =>
  [...SERIES].reverse().find(({ key }) => period[key] > 0)?.key

// The mock spaces its thousands ("2 750", "1 200") rather than using the comma
// the rest of the app formats with, so the tooltip follows the mock.
const formatNumber = (value: number) =>
  value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

export const SentimentSplit: FC<SentimentSplitProps> = ({ data }) => {
  const buildTooltip = (index: number) => {
    const period = data.periods[index]
    const total = SERIES.reduce((sum, { key }) => sum + period[key], 0)

    const rows = SERIES.map(({ color, key, label }) => {
      // Derived, not carried in the data: a hard-coded share would drift from
      // the values beside it the moment either one changed.
      const share = total ? Math.round((period[key] / total) * 100) : 0

      return `
        <div class="${styles.tooltipRow}">
          <span class="${styles.tooltipDot}" style="background:${color}"></span>
          <span class="${styles.tooltipLabel}">${label} - ${share}%</span>
          <span class="${styles.tooltipValue}">${formatNumber(
            period[key]
          )}</span>
        </div>`
    }).join('')

    return `
      <div class="${styles.tooltip}">
        <div class="${styles.tooltipTitle}">Total Mentions</div>
        <div class="${styles.tooltipTotal}">${formatNumber(total)}</div>
        ${rows}
      </div>`
  }

  const options: EChartOption = {
    grid: {
      // With containLabel this is the air BELOW the date labels, so anything
      // above zero is dead canvas between them and the legend underneath.
      bottom: 0,
      containLabel: true,
      left: 8,
      right: 8,
      top: 8,
    },
    series: SERIES.map(({ color, key, label }) => ({
      barWidth: BAR_WIDTH,
      // A band with no mentions is dropped rather than stacked as a zero: at
      // zero height it still printed its "0" — which is where the stray zeros
      // above and below the bar came from — and it took the rounded corner
      // with it, leaving the visible top of the stack square.
      data: data.periods.map((period) => {
        if (!period[key]) {
          return null
        }

        return {
          itemStyle: {
            // Rounds whichever band actually ends the column, not whichever
            // one the palette happens to list last.
            borderRadius: key === topBandOf(period) ? BAR_RADIUS : 0,
            color,
          },
          value: period[key],
        }
      }),
      itemStyle: {
        // A hairline of the card's own white between the bands, so two
        // neighbours of similar weight still read as separate counts.
        borderColor: '#ffffff',
        borderWidth: 1,
        color,
      },
      label: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 10,
        fontWeight: 400,
        // Inside the band it sits in, as in the mock — the number labels the
        // segment rather than the column.
        position: 'inside',
        show: true,
      },
      name: label,
      stack: 'sentiment',
      type: 'bar',
      // The bundled echarts types date from v4 and declare a bar's data item as
      // value + textStyle only, with no per-item itemStyle — which v5 does
      // support and the rounding above relies on.
    })) as EChartOption.SeriesBar[],
    tooltip: {
      // No pointer line: the default one drew a shadow band down the middle of
      // the plot, which read as part of the chart rather than as a cursor.
      axisPointer: { type: 'none' },
      // The whole card is drawn by the formatter, so ECharts' own chrome is
      // switched off rather than styled around.
      backgroundColor: 'transparent',
      borderWidth: 0,
      extraCssText: 'box-shadow: none; padding: 0;',
      formatter: (params) => {
        const list = Array.isArray(params) ? params : [params]

        return buildTooltip(list[0]?.dataIndex ?? 0)
      },
      // Pinned to the top of the plot and centred on the hovered column, as in
      // the mock, rather than trailing the cursor down the bar.
      // The bundled echarts types declare `size` as a bare object, so the
      // callback annotates what it actually receives.
      position: (
        point: (number | string)[],
        _params: unknown,
        _dom: unknown,
        _rect: unknown,
        size: object
      ) => {
        const { contentSize } = size as { contentSize: number[] }

        return [Number(point[0]) - contentSize[0] / 2, 0]
      },
      trigger: 'axis',
    },
    xAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        fontWeight: 400,
        margin: 16,
      },
      axisLine: { lineStyle: { color: '#25262d' } },
      axisTick: { show: false },
      data: data.periods.map((period) => period.label),
      // The dashed uprights in the mock sit on the category boundaries, not
      // under the bars, which is what `boundaryGap` + splitLine gives.
      splitLine: {
        lineStyle: { color: '#e6ebfa', type: 'dashed' },
        show: true,
      },
      type: 'category',
    },
    yAxis: {
      axisLabel: {
        color: '#86949d',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 14,
        // Bare numbers on the scale, as in the mock — ECharts would otherwise
        // group them ("4,000"), which the tooltip does but the axis does not.
        formatter: (value: number) => `${value}`,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      // The scale follows the data. Figma's 0–4000 belonged to its placeholder
      // numbers; pinned, a campaign with a dozen mentions drew nothing.
      min: 0,
      // Mentions are counted, so the scale steps in whole ones — a campaign
      // with two of them was being measured in halves.
      minInterval: 1,
      splitLine: { lineStyle: { color: '#e6ebfa', type: 'dashed' } },
      type: 'value',
    },
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>{COPY.title}</h3>
        <p className={styles.subtitle}>{COPY.subtitle}</p>
      </div>

      <div className={styles.chart}>
        <EChart height={CHART_HEIGHT} options={options} />
      </div>

      <div className={styles.legend}>
        {SERIES.map(({ color, label }) => (
          <span className={styles.legendItem} key={label}>
            <span className={styles.legendDot} style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>

      <div className={styles.insight}>
        <LightbulbIcon className={styles.insightIcon} />
        <p className={styles.insightText}>{data.insight}</p>
      </div>
    </div>
  )
}
